import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/content";
import { scrollToId } from "../lib/scroll";
import { LogoMark } from "./Logo";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="navbar-container"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled || open
          ? "bg-[#070D1D]/85 backdrop-blur-xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
        >
          <span className="group-hover:opacity-80 transition-opacity duration-300">
            <LogoMark className="w-9 h-9" testId="nav-logo-mark" animate />
          </span>
          <span className="text-left leading-none">
            <span className="block font-serif text-lg tracking-[0.18em] text-[#F8F9FA]">CALLIOPE</span>
            <span className="block font-mono text-[9px] uppercase tracking-[0.32em] text-slate-400 mt-1">
              Life Sciences Advisory
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links-desktop">
          {NAV_LINKS.slice(1).map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-[13px] font-medium tracking-wide text-slate-300 hover:text-teal-300 transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-button"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white text-[13px] font-semibold tracking-wide px-5 py-2.5 transition-colors duration-300"
          >
            Book a Strategic Conversation
            <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-slate-200 p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#070D1D]/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 space-y-4" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => go(l.id)}
              className="block w-full text-left text-slate-200 text-base py-1.5 hover:text-teal-300 transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-cta"
            onClick={() => go("contact")}
            className="w-full bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold px-5 py-3 mt-2 transition-colors"
          >
            Book a Strategic Conversation
          </button>
        </div>
      )}
    </header>
  );
};
